import React, { useState } from 'react';
import { Table, Tooltip, Button, Modal, Descriptions, Tag, message } from 'antd';
import { HistoryOutlined } from '@ant-design/icons';
import useColumnSearch from '../../ui-components/table-column-search';
import { isValidArray, isValid, sortJsonObject } from '../../utils/utilities';
import { getAgentUpdateLogsById } from '../../actions/agents.actions';
import moment from 'moment';

const getPrivileges = (data) => {
    let privileges = [], privilegesInfo = [];
    if (isValidArray(data) && data.length > 0) {
        data.forEach(element => {
            if (privileges.indexOf(element.privilege) == -1) {
                privileges.push(element.privilege);
                privilegesInfo.push({
                    text: element.privilege,
                    value: element.privilege
                });
            }
        });
    }
    return privilegesInfo;
}

const isAgentActive = (record) => {
    return isValid(record) && record.is_active != false && record.is_active != 0;
}

export const AgentsTableComponent = (props) => {
    const {
        agents,
        formData,
        updateformData    
    } = props;

    const [isLogsModalVisible, setIsLogsModalVisible] = useState(false);
    const [logsData, setLogsData] = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [selectedAgentName, setSelectedAgentName] = useState('');

    const handleShowLogs = (record, e) => {
        e.stopPropagation(); // Prevent row click event
        setSelectedAgentName(record.name || record.user_name || '');
        setLoadingLogs(true);
        setIsLogsModalVisible(true);
        
        getAgentUpdateLogsById(record.id, (data) => {
            let fetchedLogs = data.logs || [];
            if (isValidArray(fetchedLogs) && fetchedLogs.length > 0) {
                fetchedLogs = sortJsonObject(fetchedLogs, 'id', true).reverse();
            }
            setLogsData(data.logs || []);
            setLoadingLogs(false);
        }, (error) => {
            message.error(error || 'Failed to fetch agent update logs');
            setLoadingLogs(false);
            setIsLogsModalVisible(false);
        });
    };

    const handleCloseLogsModal = () => {
        setIsLogsModalVisible(false);
        setLogsData([]);
        setSelectedAgentName('');
    };

    const formatFieldName = (fieldName) => {
        return fieldName
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const formatValue = (value) => {
        if (value === null || value === undefined) {
            return 'N/A';
        }
        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        }
        return String(value);
    };

    const agentColumns = [
        {
            title: 'User/Agent Name',
            dataIndex: 'name',
            key: 'name',
            ...useColumnSearch({ dataIndexExt: "name" }),
            render: (text, record) => (
                <Tooltip title={record.user_name} placement="top">
                    <span>{text}</span>
                </Tooltip>
            )
        },
        {
            title: 'Privilege',
            dataIndex: 'privilege',
            key: 'privilege',
            filters: getPrivileges(agents),
            onFilter: (value, record) => record.privilege.indexOf(value) === 0
        },
        {
            title: 'Location',
            dataIndex: 'location',
            key: 'location',
            ...useColumnSearch({ dataIndexExt: "location" })
        },
        {
            title: 'Phone Number',
            dataIndex: 'phone_number',
            key: 'phone_number'
        },
        {
            title: 'Commision',
            children: [
                {
                    title: 'Non A/C',
                    children: [
                        {
                            title: '₹',
                            dataIndex: 'commission',
                            key: 'commission',
                        },
                        {
                            title: '%',
                            dataIndex: 'commission_percentage',
                            key: 'commission_percentage',
                        },
                    ],
                },
                {
                    title: 'A/C',
                    children: [
                        {
                            title: '₹',
                            dataIndex: 'ac_commission',
                            key: 'ac_commission',
                        },
                        {
                            title: '%',
                            dataIndex: 'ac_commission_percentage',
                            key: 'ac_commission_percentage',
                        },
                    ],
                },
                // {
                //     title: 'Scania/Volvo',
                //     children: [
                //         {
                //             title: '₹',
                //             dataIndex: 'hitech_commission',
                //             key: 'hitech_commission',
                //         },
                //         {
                //             title: '%',
                //             dataIndex: 'hitech_commission',
                //             key: 'hitech_commission',
                //         }
                //     ],
                // }
            ],
        },
        {
            title: 'Status',
            key: 'status',
            dataIndex: 'is_active',
            filters: [
                { text: 'Active', value: true },
                { text: 'In-active', value: false }
            ],
            onFilter: (value, record) => isAgentActive(record) === value,
            render: (text, record) => (
                isAgentActive(record)
                    ? <Tag color="green">Active</Tag>
                    : <Tag color="red">In-active</Tag>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 100,
            render: (text, record) => (
                <Tooltip title="Show Update Logs">
                    <Button 
                        type="link" 
                        size="large"
                        icon={<HistoryOutlined />}
                        onClick={(e) => handleShowLogs(record, e)}
                    />
                </Tooltip>
            )
        }

    ];

    return (
        <div style={{ overflowX: 'auto' }}>
            <Table 
                columns={agentColumns}
                pagination={{ pageSize: 5 }}
                dataSource={agents}
                onRow={(record, rowIndex) => {
                    return {
                        onClick: event => {
                            updateformData(record);
                        },
                    };
                }}
                rowClassName={(record, index) => {
                    let rowClassName = "";
                    if (record.id == formData.id) {
                        rowClassName = rowClassName + " TableRowClicked"
                    }
                    if (!isAgentActive(record)) {
                        rowClassName = rowClassName + " InactiveAgentRow"
                    }
                    return rowClassName
                }}
                bordered 
                scroll={{ x: 'max-content' }}
                size="small"
            />
            <Modal
                title={`Update Logs - ${selectedAgentName}`}
                visible={isLogsModalVisible}
                onCancel={handleCloseLogsModal}
                footer={[
                    <Button key="close" onClick={handleCloseLogsModal}>
                        Close
                    </Button>
                ]}
                width={800}
            >
                {loadingLogs ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>Loading logs...</div>
                ) : isValidArray(logsData) && logsData.length > 0 ? (
                    <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                        {logsData.map((log, index) => (
                            <div key={log.id || index} style={{ marginBottom: '24px', padding: '16px', border: '1px solid #f0f0f0', borderRadius: '4px' }}>
                                <Descriptions 
                                    title={`Update #${index+1} - ${moment(log.operation_time).format('DD MMM YYYY, HH:mm:ss')}`}
                                    bordered
                                    size="small"
                                    column={1}
                                >
                                    <Descriptions.Item label="Operation Time">
                                        {moment(log.operation_time).format('DD MMM YYYY, HH:mm:ss')}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Updated By">
                                        {isValid(log.updated_by) ? log.updated_by : 'N/A'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Changed Fields">
                                        {isValidArray(log.changedFieldKeys) && log.changedFieldKeys.length > 0 ? (
                                            <div>
                                                {log.changedFieldKeys.map((fieldKey, idx) => (
                                                    <Tag key={idx} color="blue" style={{ marginBottom: '4px' }}>
                                                        {formatFieldName(fieldKey)}
                                                    </Tag>
                                                ))}
                                            </div>
                                        ) : 'No changes'}
                                    </Descriptions.Item>
                                </Descriptions>
                                {isValidArray(log.changedFieldKeys) && log.changedFieldKeys.length > 0 && (
                                    <div style={{ marginTop: '16px' }}>
                                        <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Field Changes:</div>
                                        <Descriptions bordered size="small" column={1}>
                                            {log.changedFieldKeys.map((fieldKey) => {
                                                const fieldData = log[fieldKey];
                                                return (
                                                    <Descriptions.Item 
                                                        key={fieldKey} 
                                                        label={formatFieldName(fieldKey)}
                                                    >
                                                        <Tag color="green">
                                                            {formatValue(fieldData?.value)}
                                                        </Tag>
                                                    </Descriptions.Item>
                                                );
                                            })}
                                        </Descriptions>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '20px' }}>No update logs found for this agent.</div>
                )}
            </Modal>
        </div>
    );
}
